import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Slider from "@mui/material/Slider";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";

import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoIcon from "@mui/icons-material/InfoOutline";

import { useUserAuth } from "../contexts/authContext";

export function MapSettings() {
    const [editingMapSettings, setEditingMapSettings] = useState(false);

    const [latitudeSetting, setLatitudeSetting] = useState(0);
    const [latitudeError, setLatitudeError] = useState("");
    const [longitudeSetting, setLongitudeSetting] = useState(0);
    const [longitudeError, setLongitudeError] = useState("");

    const [mapBrightnessSetting, setMapBrightnessSetting] = useState(0);
    const [weatherOpacitySetting, setWeatherOpacitySetting] = useState(0);

    const [wellClearVolumeSetting, setWellClearVolumeSetting] = useState(false);
    const [wellClearVolumeRadiusNm, setWellClearVolumeRadiusNm] = useState(3);
    const [wellClearVolumeRadiusErrorText, setWellClearVolumeRadiusErrorText] = useState("");

    const [secondWellClearVolumeVisibilitySetting, setSecondWellClearVolumeVisibilitySetting] = useState(false);
    const [secondWellClearVolumeRadiusNm, setSecondWellClearVolumeRadiusNm] = useState(10);
    const [secondWellClearVolumeRadiusErrorText, setSecondWellClearVolumeRadiusErrorText] = useState("");

    const [entityLabelSetting, setEntityLabelSetting] = useState("");
    const [entitySizeSetting, setEntitySizeSetting] = useState(0);
    const [trackLabelColor, setTrackLabelColor] = useState("WHITE");
    const [trackLabelIdentifier, setTrackLabelIdentifier] = useState("SQUAWK_CODE");

    const [predLineLengthSetting, setPredLineLengthSetting] = useState(0);
    const [predLineSetting, setPredLineSetting] = useState(false);
    const [histLineSetting, setHistLineSetting] = useState(false);
    const [volumeLabelSetting, setVolumeLabelSetting] = useState(false);
    const [volumeOpacitySetting, setVolumeOpacitySetting] = useState(0);

    const [zcaDataBlockVisibilitySetting, setZcaDataBlockVisibilitySetting] = useState(false);
    const [trackLabelRotationDegSetting, setTrackLabelRotationDegSetting] = useState(270);

    const { userMapSettings, updateMapSettings, setSnackbar } = useUserAuth();

    useEffect(() => {
        if (userMapSettings) {
            initalizeUserMapSettings(userMapSettings);
        }
    }, []);

    const initalizeUserMapSettings = (settings) => {
        setLatitudeSetting(settings.latitude);
        setLongitudeSetting(settings.longitude);

        setMapBrightnessSetting(settings.brightness);
        setWeatherOpacitySetting(settings.weather_opacity);

        setWellClearVolumeSetting(settings.well_clear_volume);
        setWellClearVolumeRadiusNm(settings.well_clear_volume_radius_nm);

        setSecondWellClearVolumeVisibilitySetting(settings.second_well_clear_volume_visibility);
        setSecondWellClearVolumeRadiusNm(settings.second_well_clear_volume_radius_nm);

        setEntityLabelSetting(settings.label_visible);
        setEntitySizeSetting(settings.entity_size);
        setTrackLabelColor(settings.track_label_color);
        setTrackLabelIdentifier(settings.track_label_identifier);

        setPredLineLengthSetting(settings.pred_line_lead_time);
        setPredLineSetting(settings.pred_line);
        setHistLineSetting(settings.hist_line);
        setVolumeLabelSetting(settings.volume_labels);
        setVolumeOpacitySetting(settings.op_opacity);

        setTrackLabelRotationDegSetting(settings.track_label_rotation_deg);
        setZcaDataBlockVisibilitySetting(settings.zca_data_block_visibility);
    };

    const handleResetClick = () => {
        if (!confirm("This will reset your saved settings to the default values. Click OK if you would like to proceed")) {
            return;
        }

        const mapSettings = {
            ...userMapSettings,
            label_visible: "off",
            pred_line_lead_time: 20,
            pred_line: false,
            hist_line: false,
            well_clear_volume: false,
            volume_labels: false,
            op_opacity: 0.5,
            latitude: 39.9612,
            longitude: -82.9988,
            entity_size: 0.3,
            brightness: 1,
            weather_opacity: 1,
            track_label_color: "WHITE",
            track_label_identifier: "SQUAWK_CODE",
            well_clear_volume_radius_nm: 3,
            second_well_clear_volume_visibility: false,
            second_well_clear_volume_radius_nm: 10,
            zca_data_block_visibility: true,
            track_label_rotation_deg: 270
        };
        initalizeUserMapSettings(mapSettings);
        updateMapSettings(mapSettings);
        setSnackbar({ children: "Settings successfully updated", severity: "success" });
    };

    const handleCancelMapSettings = () => {
        setLatitudeError("");
        setLongitudeError("");
        setWellClearVolumeRadiusErrorText("");
        setSecondWellClearVolumeRadiusErrorText("");

        initalizeUserMapSettings(userMapSettings);
        setEditingMapSettings(false);
    };

    const handleSaveMapSettings = () => {
        const latitude = parseFloat(latitudeSetting);
        const longitude = parseFloat(longitudeSetting);
        const wellClearVolumeRadius = parseFloat(wellClearVolumeRadiusNm);
        const secondWellClearVolumeRadius = parseFloat(secondWellClearVolumeRadiusNm);

        setLongitudeError("");
        setLatitudeError("");
        setWellClearVolumeRadiusErrorText("");
        setSecondWellClearVolumeRadiusErrorText("");

        let error = false;
        if (isNaN(latitude) || latitude > 90 || latitude < -90) {
            setLatitudeError(`Please enter a latitude greater than -90 and less than 90`);
            error = true;
        }
        if (isNaN(longitude) || longitude > 180 || longitude < -180) {
            setLongitudeError("Please enter a longitude greater than -180 and less than 180");
            error = true;
        }
        if (isNaN(wellClearVolumeRadius) || wellClearVolumeRadius > 5 || wellClearVolumeRadius < 0.01) {
            setWellClearVolumeRadiusErrorText("Please enter a radius greater than 0.01 and less than 5 nautical miles");
            error = true;
        }
        if (isNaN(secondWellClearVolumeRadius) || secondWellClearVolumeRadius > 12 || secondWellClearVolumeRadius <= wellClearVolumeRadius) {
            const errorText = "Please enter a radius greater than the inner well clear volume and less than 12 nautical miles";
            setSecondWellClearVolumeRadiusErrorText(errorText);
            error = true;
        }
        if (error) return;

        const mapSettings = {
            ...userMapSettings,
            label_visible: entityLabelSetting,
            pred_line_lead_time: predLineLengthSetting,
            pred_line: predLineSetting,
            hist_line: histLineSetting,
            well_clear_volume: wellClearVolumeSetting,
            volume_labels: volumeLabelSetting,
            op_opacity: volumeOpacitySetting,
            latitude: latitude,
            longitude: longitude,
            entity_size: parseFloat(entitySizeSetting),
            brightness: parseFloat(mapBrightnessSetting),
            weather_opacity: parseFloat(weatherOpacitySetting),
            track_label_color: trackLabelColor,
            track_label_identifier: trackLabelIdentifier,
            well_clear_volume_radius_nm: wellClearVolumeRadius,
            second_well_clear_volume_visibility: secondWellClearVolumeVisibilitySetting,
            second_well_clear_volume_radius_nm: secondWellClearVolumeRadiusNm,
            track_label_rotation_deg: trackLabelRotationDegSetting,
            zca_data_block_visibility: zcaDataBlockVisibilitySetting
        };
        updateMapSettings(mapSettings);
        setEditingMapSettings(false);
        setSnackbar({ children: "Settings successfully updated", severity: "success" });
    };
    const handleWellClearVolumeRadiusChange = (e) => {
        const VALID_NUMBER_FIELDS_REGEX = /^-?(\d*\.?\d*)?$/;
        if (VALID_NUMBER_FIELDS_REGEX.test(e.target.value)) {
            setWellClearVolumeRadiusNm(e.target.value);
        }
    };
    const handleSecondWellClearVolumeRadiusChange = (e) => {
        const VALID_NUMBER_FIELDS_REGEX = /^-?(\d*\.?\d*)?$/;
        if (VALID_NUMBER_FIELDS_REGEX.test(e.target.value)) {
            setSecondWellClearVolumeRadiusNm(e.target.value);
        }
    };
    return (
        <Paper sx={{ p: 2, display: "flex", flexDirection: "column", maxWidth: "800px" }}>
            <Box sx={{ display: "flex", flex: "row", justifyContent: "space-between" }}>
                <Typography variant="h6" noWrap>
                    Map Settings
                </Typography>
                <Box sx={{ display: "flex" }}>
                    {editingMapSettings === true ? (
                        <>
                            <Tooltip title="Save">
                                <IconButton id="save" onClick={handleSaveMapSettings}>
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton onClick={handleCancelMapSettings}>
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            <Tooltip title="Edit">
                                <IconButton id="edit" onClick={() => setEditingMapSettings(true)}>
                                    <ModeEditIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset">
                                <IconButton onClick={handleResetClick} data-testid="reset">
                                    <RestartAltIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Default Map Settings */}
                <Box>
                    <Typography gutterBottom>Home Location:</Typography>
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                        <TextField
                            variant="standard"
                            label="Latitude"
                            value={latitudeSetting}
                            onChange={(e) => setLatitudeSetting(e.target.value)}
                            type="number"
                            sx={{ margin: "8px 0", flex: 1 }}
                            InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }}
                            disabled={editingMapSettings === false}
                            size="small"
                            onWheel={(e) => e.target.blur()}
                            error={latitudeError !== ""}
                            helperText={latitudeError}
                            id="latitudeInput"
                        />
                        <TextField
                            variant="standard"
                            label="Longitude"
                            value={longitudeSetting}
                            onChange={(e) => setLongitudeSetting(e.target.value)}
                            type="number"
                            sx={{ margin: "8px 0", flex: 1 }}
                            InputProps={{ endAdornment: <InputAdornment position="end">deg</InputAdornment> }}
                            disabled={editingMapSettings === false}
                            size="small"
                            onWheel={(e) => e.target.blur()}
                            error={longitudeError !== ""}
                            helperText={longitudeError}
                            id="longitudeInput"
                        />
                    </Box>
                </Box>

                {/* Entity Settings */}
                <Typography>Entity Settings:</Typography>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 }}>
                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="entity-label">Entity Labels</InputLabel>
                        <Select
                            labelId="entity-label"
                            id="entity-labels"
                            value={entityLabelSetting}
                            onChange={(e) => setEntityLabelSetting(e.target.value)}
                            label="Entity Labels"
                        >
                            <MenuItem value={"on"}>Visible</MenuItem>
                            <MenuItem value={"off"}>On Hover</MenuItem>
                            <MenuItem value={"alerts"}>In Alert Volumes</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="label-identifer">Label Identifier</InputLabel>
                        <Select
                            value={trackLabelIdentifier}
                            labelId="label-identifer"
                            label="Label Identifier"
                            onChange={(e) => setTrackLabelIdentifier(e.target.value)}
                        >
                            <MenuItem value={"SQUAWK_CODE"}>Squawk Code</MenuItem>
                            <MenuItem value={"CALLSIGN"}>Callsign</MenuItem>
                            <MenuItem value={"TRACK_LABEL"}>Track Label</MenuItem>
                            <MenuItem value={"NONE"}>None</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="label-color">Label Color</InputLabel>
                        <Select value={trackLabelColor} labelId="label-color" label="Label Color" onChange={(e) => setTrackLabelColor(e.target.value)}>
                            <MenuItem value={"WHITE"}>White</MenuItem>
                            <MenuItem value={"GREEN"}>Green</MenuItem>
                            <MenuItem value={"YELLOW"}>Yellow</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel
                            id="label-rotation-label"
                            sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 1 }}
                        >
                            Label Rotation
                            <Tooltip title="Rotation is calculated with respect to the vehicles heading." placement="top">
                                <InfoIcon fontSize="small" color="disabled" />
                            </Tooltip>
                        </InputLabel>
                        <Select
                            labelId="label-rotation-label"
                            id="label-rotation"
                            value={trackLabelRotationDegSetting}
                            onChange={(e) => setTrackLabelRotationDegSetting(e.target.value)}
                            label="Label Rotation"
                        >
                            {[0, 45, 90, 135, 180, 225, 270, 315, 360].map((value, i) => (
                                <MenuItem key={i} value={value}>
                                    {value}⁰
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="pred-line-label">Prediction Lines</InputLabel>
                        <Select
                            labelId="pred-line-label"
                            id="pred-line"
                            value={predLineSetting}
                            label="Prediction Lines"
                            onChange={(e) => setPredLineSetting(e.target.value)}
                        >
                            <MenuItem value={true}>Visible</MenuItem>
                            <MenuItem value={false}>Hidden</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="hist-line-label">History Lines</InputLabel>
                        <Select
                            labelId="hist-line-label"
                            id="hist-line"
                            value={histLineSetting}
                            label="History Lines"
                            onChange={(e) => setHistLineSetting(e.target.value)}
                        >
                            <MenuItem value={true}>Visible</MenuItem>
                            <MenuItem value={false}>Hidden</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="volume-label">Volume Labels</InputLabel>
                        <Select
                            labelId="volume-label"
                            id="volume-labels"
                            value={volumeLabelSetting}
                            onChange={(e) => setVolumeLabelSetting(e.target.value)}
                            label="Volume Labels"
                        >
                            <MenuItem value={true}>Visible</MenuItem>
                            <MenuItem value={false}>Hidden</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="conflict-alert-visibility-setting-label">Conflict Alert Data Block</InputLabel>
                        <Select
                            labelId="conflict-alert-visibility-setting-label"
                            id="conflict-alert-visibility-setting"
                            value={zcaDataBlockVisibilitySetting}
                            onChange={(e) => setZcaDataBlockVisibilitySetting(e.target.value)}
                            label="Conflict Alert Data Block"
                        >
                            <MenuItem value={true}>Visible</MenuItem>
                            <MenuItem value={false}>Hidden</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Typography>Well Clear Volumes:</Typography>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="well-clear-volume-visibility-label">Inner Visibility</InputLabel>
                        <Select
                            labelId="well-clear-volume-visibility-label"
                            id="well-clear-volume-visibility"
                            value={wellClearVolumeSetting}
                            onChange={(e) => setWellClearVolumeSetting(e.target.value)}
                            label="Inner Visibility"
                        >
                            <MenuItem value={true}>Visible</MenuItem>
                            <MenuItem value={false}>Hidden</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        variant="standard"
                        label="Inner Radius"
                        value={wellClearVolumeRadiusNm}
                        onChange={handleWellClearVolumeRadiusChange}
                        slotProps={{ input: { endAdornment: <InputAdornment position="end">nm</InputAdornment> } }}
                        disabled={editingMapSettings === false}
                        size="small"
                        error={wellClearVolumeRadiusErrorText !== ""}
                        helperText={wellClearVolumeRadiusErrorText}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                    <FormControl sx={{ flex: 1 }} disabled={editingMapSettings === false} size="small" variant="standard">
                        <InputLabel id="second-well-clear-volume-visibility-label">Outer Visibility</InputLabel>
                        <Select
                            labelId="second-well-clear-volume-visibility-label"
                            id="second-well-clear-volume-visibility"
                            value={secondWellClearVolumeVisibilitySetting}
                            onChange={(e) => setSecondWellClearVolumeVisibilitySetting(e.target.value)}
                            label="Outer Visibility"
                        >
                            <MenuItem value={true}>Visible</MenuItem>
                            <MenuItem value={false}>Hidden</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        variant="standard"
                        label="Outer Radius"
                        value={secondWellClearVolumeRadiusNm}
                        onChange={handleSecondWellClearVolumeRadiusChange}
                        slotProps={{ input: { endAdornment: <InputAdornment position="end">nm</InputAdornment> } }}
                        disabled={editingMapSettings === false}
                        size="small"
                        error={secondWellClearVolumeRadiusErrorText !== ""}
                        helperText={secondWellClearVolumeRadiusErrorText}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box>
                    <Typography gutterBottom>Entity Size:</Typography>
                    <Slider
                        valueLabelDisplay="auto"
                        value={entitySizeSetting}
                        onChange={(e) => setEntitySizeSetting(e.target.value)}
                        step={0.05}
                        min={0.25}
                        max={1}
                        marks
                        disabled={editingMapSettings === false}
                    />
                </Box>

                <Box>
                    <Typography gutterBottom>{"Prediction Line Length (seconds):"}</Typography>
                    <Slider
                        valueLabelDisplay="auto"
                        value={predLineLengthSetting}
                        onChange={(e) => setPredLineLengthSetting(e.target.value)}
                        max={120}
                        step={5}
                        min={10}
                        marks
                        disabled={editingMapSettings === false}
                    />
                </Box>

                <Box>
                    <Typography gutterBottom>Volume Opacity:</Typography>
                    <Slider
                        valueLabelDisplay="auto"
                        value={volumeOpacitySetting}
                        onChange={(e) => setVolumeOpacitySetting(e.target.value)}
                        step={0.1}
                        min={0}
                        max={1}
                        marks
                        disabled={editingMapSettings === false}
                    />
                </Box>

                <Box>
                    <Typography gutterBottom>Weather Opacity:</Typography>
                    <Slider
                        valueLabelDisplay="auto"
                        value={weatherOpacitySetting}
                        onChange={(e) => setWeatherOpacitySetting(e.target.value)}
                        step={0.1}
                        min={0}
                        max={1}
                        marks
                        disabled={editingMapSettings === false}
                    />
                </Box>

                <Box>
                    <Typography gutterBottom>Map Brightness:</Typography>
                    <Slider
                        valueLabelDisplay="auto"
                        value={mapBrightnessSetting}
                        onChange={(e) => setMapBrightnessSetting(e.target.value)}
                        step={0.1}
                        min={0}
                        max={1}
                        marks
                        disabled={editingMapSettings === false}
                    />
                </Box>
            </Box>
        </Paper>
    );
}

export default MapSettings;
